// 본질(essence): 원국에서 계산하는 값 한 벌. 이벤트·직업·소품·결과 연결은 원국을 다시 해석하지 않고 이 값만 읽는다.
// 설계: docs/superpowers/specs/2026-10-19-saju-story-linkage-design.md 4절, 명세: ai/knowledge/essence-spec.md
// 모든 값은 { value, cardIds, tier } 이다. tier 'classic' 은 고서 카드를 근거로 한 구조이고, 'game' 은 게임용 추정이다.
// 카드는 모두 draft 이며 원문 확인은 사용자가 한다 (ai/knowledge/cards.json).
import { sajuProfile } from './profile.js';

const GROUPS = ['비겁', '식상', '재성', '관성', '인성'];
const TEN = [['비견', '겁재'], ['식신', '상관'], ['편재', '정재'], ['편관', '정관'], ['편인', '정인']];

// 격국 → 순용·역용 분류 (서사 축 id 는 격국 이름과 같다). needsCheck: 자평진전 원전 확인 전의 초안 분류.
// 이 표는 ai/knowledge/essence-links.json 의 yong 과 같아야 하며 ai/scripts/check-links.mjs 가 비교한다.
export const YONG_BY_GYEOK = {
  정관격: { value: '순용' }, 정재격: { value: '순용' }, 정인격: { value: '순용' }, 식신격: { value: '순용' },
  편재격: { value: '순용', needsCheck: true },
  편관격: { value: '역용' }, 상관격: { value: '역용' }, 편인격: { value: '역용' }, 양인격: { value: '역용' },
  건록격: { value: '별도', needsCheck: true }, 월겁격: { value: '별도', needsCheck: true },
};
export const ARC_IDS = Object.keys(YONG_BY_GYEOK);

// 격국 → 근거 카드 (link-map.json 의 byProfile.gyeok 와 새 카드)
const GYEOK_CARDS = {
  정관격: ['JP-정관'], 편관격: ['JP-편관'], 정인격: ['JP-정인'], 편인격: ['JP-편인'],
  식신격: ['JP-식신'], 상관격: ['JP-상관'], 정재격: ['JP-정재'], 편재격: ['JP-편재'],
  건록격: ['JP-건록', 'JP-건록월겁'], 월겁격: ['JP-월겁', 'JP-건록월겁'], 양인격: ['JP-양인'],
};
const SEASON_CARD = { 겨울: 'GT-겨울', 여름: 'GT-여름', 봄: 'GT-봄가을', 가을: 'GT-봄가을' };
const JOHU_CARD = { 화: 'GT-겨울', 수: 'GT-여름', 없음: 'GT-봄가을' };

const entry = (value, cardIds, tier, extra = {}) => ({ value, cardIds, tier, ...extra });

function groupShare(saju) {
  const force = saju?.statBasis?.tenGodForces;
  if (!force) return null;
  const sums = TEN.map((pair) => pair.reduce((total, key) => total + force[key], 0));
  const total = sums.reduce((a, b) => a + b, 0);
  if (!sums.every(Number.isFinite) || !(total > 0)) return null;
  return Object.fromEntries(GROUPS.map((g, i) => [g, sums[i] / total]));
}

/** 원국(서버 응답)에서 본질을 만든다. 일간·월지를 모르면 null. 시주를 모르면 시주에 영향받는 값에 provisional 을 붙인다. */
export function essenceOf(saju) {
  const profile = sajuProfile(saju);
  if (!profile || !profile.gyeok) return null;
  const hourKnown = Boolean(saju.hour);
  const provisional = hourKnown ? {} : { provisional: true };
  const gyeok = profile.gyeok.name;
  const yong = YONG_BY_GYEOK[gyeok];
  const share = groupShare(saju);
  return {
    version: 'essence-v1',
    hourKnown,
    gyeok: entry(gyeok, GYEOK_CARDS[gyeok], 'classic', { basis: profile.gyeok.basis }),
    yong: entry(yong.value, ['JP-순용역용'], 'classic', yong.needsCheck ? { needsCheck: true } : {}),
    arcId: entry(gyeok, GYEOK_CARDS[gyeok], 'classic'),
    dayStem: entry(profile.dayStem, [`DC-stem-${profile.dayStem}`], 'classic', { element: profile.dayElement, polarity: profile.polarity }),
    season: entry(profile.season, [SEASON_CARD[profile.season]], 'classic', { transition: profile.transition }),
    // 지원 구간·한난조습·세력 비중은 고서의 수치 공식이 아니라 게임용 추정이다.
    support: entry(profile.support.band, ['DC-strength'], 'game', provisional),
    climate: entry(profile.climate ? { warm: profile.climate.warm, dry: profile.climate.dry } : null, [], 'game', provisional),
    johuNeed: entry(profile.johu.need, [JOHU_CARD[profile.johu.need]], 'game', provisional),
    groupShare: entry(share, GROUPS.map((g) => `YH-${g}`), 'game', provisional),
    limits: profile.limits,
  };
}
